/* ==========================================================================
   MOTEUR v2 - simulateur de durée de congélation
   --------------------------------------------------------------------------
   Réécrit le 2026-08-20 après arbitrage du dev. Trois principes :
     1. Toutes les durées sont ramenées en TOUT COMPRIS (mise à cœur + exposition).
        Mélanger de l'exposition seule et du tout-compris sur la même échelle
        était le défaut de la v1.
     2. Aucune durée ne dépend de la NATURE des objets : nous n'avons pas de quoi
        la calculer. La nature déclenche des AVERTISSEMENTS, et disqualifie la
        branche courte dont elle rompt une condition.
     3. Les 8 h de mise à cœur sont comptées DANS CHAQUE passage, pas une fois :
        FRO10 mesure le coeur d'un chargement, pas la mise en froid de la cuve.
        Chaque nouveau passage réintroduit des objets tièdes.
   ========================================================================== */

/* --- Étoiles -> température. Règlement délégué (UE) 2019/2016, art. 2 ------ */
const T_ETOILES = { 0:0, 1:-6, 2:-12, 3:-18, 4:-18 };

/* --- Seuil de survie. FRO18 (Olson 2013), modèle ---------------------------
   « survival was estimated for temperatures above -12°C even after 1 wk ».
   La borne 2 étoiles vaut exactement -12 °C : écrire « insuffisant », jamais
   « limite ».                                                                */
const T_SURVIE = -12;

/* --- Mise à cœur d'un chargement. FRO10 (Naylor & Boase 2010) -------------
   « 2.5 kg of loosely packed, dry laundry takes ≈8 h to reach -17°C ».
   ⚠ Mesure unique, un chargement, un appareil (Zanussi Z1921/8FFA).
   FRO10 interdit nommément d'en faire une règle proportionnelle au poids :
   nous l'utilisons comme un ORDRE DE GRANDEUR par passage, pas comme une loi. */
const T_MISE_A_COEUR = 8;

/* --- Taux de remplissage --------------------------------------------------
   ⚠ NON SOURCÉ. Prudence de circulation d'air (SAC5, « placed loosely »).
   Fixé à 60 % par le dev le 2026-08-20.                                      */
const TAUX_REMPLISSAGE = 0.60;

/* --- Exposition de la branche courte. Anses FRO26 -------------------------
   « congélation de 24 à 48 heures, en s'assurant que l'objet, DANS SA
   TOTALITÉ, est bien à -20 °C ». Borne basse retenue.
   Arbitrage du dev du 2026-08-20 : l'Anses plutôt que les 2 h de l'ARS
   Île-de-France (FRO24), pour rester du côté prudent. Le corpus français
   donne cinq valeurs à -20 °C, de 2 h à 72 h ; celle-ci est la seule qui
   pose explicitement la condition de température à cœur, donc la seule qui
   se compose proprement avec les 8 h de FRO10.                               */
const EXPO_COURTE = 24;

/* --- Le corpus français à -20 °C, affiché au visiteur ---------------------- */
const CORPUS_20 = [
  { qui:"ARS Île-de-France",  val:"2 h",       ref:"FRO24", note:"exposition seule, objet déjà à cœur" },
  { qui:"Anses",              val:"24 à 48 h", ref:"FRO26", note:"exposition, objet à cœur - notre référence" },
  { qui:"Ministère de la Santé", val:"48 h",   ref:"FRO25", note:"reprend l'Anses sans sa condition de cœur" },
  { qui:"CNEV",               val:"48 à 72 h", ref:"FRO1",  note:"séjour complet dans l'appareil" },
  { qui:"Assurance Maladie",  val:"72 h",      ref:"FRO22", note:"séjour complet" },
  { qui:"Ville de Paris",     val:"72 h",      ref:"FRO23", note:"séjour complet, sac fermé" }
];

/* --- Alternatives au froid ------------------------------------------------
   ⚠ Images et URL NON INVENTÉES : images retrouvées via le skill /find-file
   dans l'index des fichiers Shopify, URL vérifiées dans
   .reference/pages_referentiel.yml le 2026-08-20. Ne pas les modifier de
   mémoire - repasser par /find-file et le référentiel.
   Les mentions chiffrées portent leur garde-fou : la tente à 49 °C dépasse le
   seuil des adultes (CHA9) mais PAS celui des œufs, qui ne cèdent qu'à la
   durée (CHA12) ; la diatomée demande 48 à 72 h de contact (TD6), elle n'est
   pas immédiate. Le dépôt en chambre froide est un service réellement rendu
   (vérifié dans .reference/site-content/ : dépôt en Île-de-France, et
   intervention nationale sur gros volume).                                    */
const ALTERNATIVES = [
  {
    titre:"Un nettoyeur vapeur certifié",
    img:"https://cdn.shopify.com/s/files/1/0102/0362/7556/files/Cimex_eradicator_Polti_TurboSafe_Nidoan.jpg?v=1755201039&width=400",
    alt:"Nettoyeur vapeur Polti Cimex Eradicator avec accessoire TurboSafe",
    desc:"C'est la méthode pour tout ce qui ne rentre pas au congélateur : matelas, sommier, plinthes, coutures, mobilier. La vapeur traite la surface là où elle est, sans rien déplacer.",
    note:"Complémentaire du froid, pas concurrente : le froid traite ce qu'on peut mettre dans un sac, la vapeur traite ce qui reste dans la pièce.",
    liens:[{t:"Voir les traitements", u:"https://allopunaise.com/collections/traitement-contre-les-punaises-de-lit"}]
  },
  {
    titre:"Un sac hydrosoluble, avec de la diatomée dedans",
    img:"https://cdn.shopify.com/s/files/1/0102/0362/7556/files/sac-hydrosoluble-punaise-de-lit-585955.jpg?v=1747381971&width=400",
    alt:"Sac hydrosoluble translucide pour linge infesté de punaises de lit",
    desc:"Pour ce que vous ne pouvez ni laver, ni congeler, ni passer à la vapeur : enfermez-le dans un sac hydrosoluble ou une housse d'oreiller, avec un peu de spray de terre de diatomée à l'intérieur. Le sac est translucide, vous gardez l'œil sur ce qu'il y a dedans.",
    note:"Sa bandelette est faite pour se dissoudre à l'eau : tant qu'elle est intacte, c'est un vrai sac de confinement. Manipulez-le peu. Comptez 48 à 72 h de contact - sur un tissu pulvérisé à la diatomée, la mortalité atteint 100 % en 48 h. Ce n'est pas immédiat, et c'est le confinement qui fait le travail.",
    liens:[{t:"Le sac hydrosoluble", u:"https://allopunaise.com/products/sac-protecteur-punaise-de-lit-hydrosoluble"},
           {t:"Le spray de diatomée", u:"https://allopunaise.com/products/terre-de-diatomee-punaise-de-lit-nidoan"},
           {t:"Les housses d'oreiller", u:"https://allopunaise.com/collections/housses-oreiller-anti-punaise-de-lit"}]
  },
  {
    titre:"Une tente chauffante, à 49 °C",
    img:"https://cdn.shopify.com/s/files/1/0102/0362/7556/files/tente-chauffante-punaise-de-lit-zappbug-657757.jpg?v=1747381940&width=400",
    alt:"Tente chauffante ZappBug pour traitement thermique des affaires",
    desc:"Le miroir du congélateur : même principe de traitement en volume, mais par le chaud. Elle prend valises, chaussures, sacs, livres, tout ce que le froid met des jours à pénétrer.",
    note:"49 °C dépasse le seuil qui tue les adultes. Les œufs, eux, ne cèdent qu'à la durée : laissez le cycle complet, un passage rapide n'est qu'un demi-traitement.",
    liens:[{t:"Voir les traitements", u:"https://allopunaise.com/collections/traitement-contre-les-punaises-de-lit"}]
  },
  {
    titre:"Le sèche-linge, pour tout ce qui le supporte",
    img:"https://cdn.shopify.com/s/files/1/0102/0362/7556/files/punaise-de-lit-seche-linge.jpg?v=1707222303&width=400",
    alt:"Main réglant la température d'un sèche-linge pour traiter du linge infesté",
    desc:"C'est la méthode la plus rapide et la mieux documentée de toutes, et vous l'avez déjà. Un cycle chaud tue adultes, nymphes et œufs sur du linge, là où le congélateur demande des jours.",
    note:"Cycle chaud, au moins 30 minutes : à 10 minutes, un cinquième des punaises survit encore. L'étude porte sur des sécheuses professionnelles ; sur une machine domestique, prenez le programme le plus chaud et ne raccourcissez pas.",
    liens:[{t:"Lavage et séchage en détail", u:"https://allopunaise.com/blogs/traitement-punaise-de-lit/punaise-de-lit-machine-a-laver"}]
  },
  {
    titre:"Déposer vos affaires en chambre froide",
    img:"https://cdn.shopify.com/s/files/1/0102/0362/7556/files/chambre-froide-punaise-de-lit.jpg?v=1718962196&width=400",
    alt:"Chambre froide industrielle utilisée pour traiter des textiles infestés",
    desc:"Quand le calcul ci-dessus vous annonce des semaines, c'est que votre congélateur est trop petit pour ce que vous avez. Une chambre froide traite tout en une fois, au lieu de vingt passages.",
    note:"Dépôt possible en Île-de-France et en banlieue parisienne. Sur un très gros volume, l'équipe peut intervenir partout en France.",
    liens:[{t:"Nous contacter", u:"https://allopunaise.com/pages/traitement-punaise-de-lit-paris"}]
  }
];

const GABARITS = [
  { id:"sac",     nom:"Grand sac poubelle",       sous:"110 L replié sans tasser, soit environ 30 L utiles", litres:30, def:1 },
  { id:"valise",  nom:"Valise cabine",            sous:"format avion, environ 40 L",                          litres:40, def:0 },
  { id:"carton",  nom:"Carton de déménagement",   sous:"standard, environ 55 L",                              litres:55, def:0 }
];

/* --- Contenu : avertissements seulement, jamais de durée ------------------ */
const CONTENU = [
  { id:"linge",   nom:"Linge, draps, vêtements en vrac",        dense:false },
  { id:"couette", nom:"Couette, couverture, plaid, oreillers",  dense:true,
    avert:"Un textile épais plafonne le froid à son cœur. Sous une couverture, la mesure la plus fine du corpus relève encore 3 °C d'écart avec l'enceinte au bout de deux heures : votre congélateur à -20 °C n'y fait que -17 °C." },
  { id:"pile",    nom:"Vêtements pliés en pile, sacs bien pleins", dense:true,
    avert:"Une pile tassée se comporte comme un bloc : le froid met beaucoup plus longtemps à en atteindre le centre. Déliassez, ou allongez la durée." },
  { id:"livres",  nom:"Livres, chaussures, sacs à dos chargés", dense:true,
    avert:"Personne n'a mesuré le temps qu'il faut au froid pour atteindre le cœur d'un objet dense. Ne vous fiez pas à la durée affichée sans une sonde placée au contact." },
  { id:"meuble",  nom:"Petit meuble, cadre, objet en bois",     dense:true,
    avert:"Un objet massif est le pire cas pour le froid, et le bois peut se fendre. La vapeur traite ces surfaces bien mieux que le congélateur." },
  { id:"peluche", nom:"Peluches, doudous",                      dense:false },
  { id:"elec",    nom:"Électronique, flacons, cosmétiques, cuir, vinyles", dense:false, refuse:true,
    avert:"À ne pas congeler : le froid les abîme, et la condensation au retour à l'ambiante finit le travail." }
];

/* Portee : tout se cherche DANS le composant. Les identifiants de la maquette
   (#go, #out, #site...) sont trop generiques pour un document Focal complet ;
   les resoudre depuis la racine evite qu'un element du theme soit capture. */
const ROOT = document.querySelector('.apc');
const $  = s => ROOT.querySelector(s);
const $$ = s => Array.from(ROOT.querySelectorAll(s));
function esc(s){ return String(s).replace(/[&<>]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c])); }
function jours(h){ return Math.round(h/24*10)/10; }
function fmtJours(h){
  const j = h/24;
  if (j < 1) return Math.round(h)+" h";
  const e = Math.floor(j), r = Math.round((j-e)*24);
  return e + (e>1?" jours":" jour") + (r>0 ? " et "+r+" h" : "");
}

/* ==========================================================================
   La durée d'un passage, ramenée en tout compris
   ========================================================================== */
function dureePassage(tApp, frostFree, quatreEtoiles, contientDense){
  const r = { tApp, miseACoeur:0, expo:0, total:0, type:null, src:"", conditions:[], manque:[] };

  /* Barrière : au-dessus de -12 °C, aucune durée ne suffit ---------------- */
  if (tApp >= T_SURVIE){ r.type = "impossible"; return r; }

  /* Branche courte, Anses : -20 °C ou moins, toutes conditions réunies ----- */
  const condTemp  = tApp <= -20;
  const condFrost = !frostFree;
  const condEtoile= quatreEtoiles;
  const condObjet = !contientDense;
  if (!condTemp)   r.manque.push("descendre à -20 °C ou moins (vous êtes à "+tApp+" °C)");
  if (!condFrost)  r.manque.push("ne pas être à froid ventilé");
  if (!condEtoile) r.manque.push("être un appareil 4 étoiles");
  if (!condObjet)  r.manque.push("ne contenir que des affaires souples, sans objet dense");

  if (condTemp && condFrost && condEtoile && condObjet){
    r.type = "descente+expo";
    r.miseACoeur = T_MISE_A_COEUR;
    r.expo = EXPO_COURTE;
    r.total = r.miseACoeur + r.expo;
    r.src = "L'Anses recommande 24 à 48 h une fois l'objet à -20 °C dans sa totalité (rapport 2021-SA-0147, 2023). Nous retenons la borne basse, plus une mise à température de l'ordre de 8 h, mesurée par Naylor et Boase sur un sac de 2,5 kg de linge (2010).";
    r.conditions = [
      "votre appareil tient bien -20 °C, sonde à l'appui",
      "il ne se dégivre pas tout seul",
      "vous ne le remplissez qu'aux deux tiers, sans tasser",
      "vos affaires sont souples et l'air circule autour"
    ];
    return r;
  }

  /* Branche tout compris : -18 °C ou moins --------------------------------- */
  if (tApp <= -18){
    r.type = "tout-compris";
    r.total = 72;
    r.src = "Le CNEV (2015), l'Assurance Maladie et la Ville de Paris recommandent 72 h. Cette durée compte le séjour complet : la mise à température du cœur est déjà dedans, et la marge absorbe le froid ventilé.";
    return r;
  }

  /* Branche Olson : de -17 °C à -13 °C ------------------------------------- */
  r.type = "descente+expo";
  r.miseACoeur = T_MISE_A_COEUR;
  if (tApp <= -16){ r.expo = 80; r.src = "Olson et ses coauteurs mesurent 80 h à -16 °C (2013), auxquelles s'ajoute une mise à température de l'ordre de 8 h."; }
  else if (tApp <= -15){ r.expo = 85; r.src = "Olson et ses coauteurs mesurent 85 h à -15 °C (2013), auxquelles s'ajoute une mise à température de l'ordre de 8 h."; }
  else { r.expo = 85; r.src = "Olson et ses coauteurs ne mesurent pas au-dessus de -15 °C. Nous retenons sa valeur la plus longue, 85 h : à cette température la durée réelle est plus longue encore."; r.plancher = true; }
  r.total = r.miseACoeur + r.expo;
  return r;
}

/* ==========================================================================
   La campagne
   ========================================================================== */
function simule(cfg){
  const choisis  = CONTENU.filter(c => cfg.contenu.includes(c.id));
  const refuses  = choisis.filter(c => c.refuse);
  const traites  = choisis.filter(c => !c.refuse);
  const denses   = traites.filter(c => c.dense);

  const r = { cfg, choisis, refuses, traites, denses, tApp:cfg.tApp };
  r.passage = dureePassage(cfg.tApp, cfg.frostFree, cfg.quatreEtoiles, denses.length > 0);

  r.vLot  = Math.max(1, Math.round(cfg.vUtile * TAUX_REMPLISSAGE));
  r.nLots = Math.max(1, Math.ceil(cfg.vObjets / r.vLot));
  r.total = (r.passage.type === "impossible") ? null : r.nLots * r.passage.total;
  return r;
}

/* ==========================================================================
   RENDU
   ========================================================================== */
function bloc(tone,label,titre,corps){
  return '<div class="apc-verdict apc-verdict--'+tone+'"><p class="apc-verdict__l">'+label+'</p>'+
    '<p class="apc-verdict__t">'+titre+'</p><p class="apc-verdict__p">'+corps+'</p></div>';
}

function rendVerdict(r){
  const p = r.passage;
  if (p.type === "impossible"){
    return bloc("ko","Verdict","Cet appareil ne peut pas traiter vos affaires",
      "Il tient <b>"+r.tApp+" °C</b>. Or la punaise de lit survit au-dessus de -12 °C, même après une semaine "+
      "d'exposition continue. <b>Aucune durée ne suffit</b>, quelle qu'elle soit : ce n'est pas une question de patience.");
  }
  return "";   /* fusionne dans rendBig - voir ci-dessous */
}

/* Verdict et duree totale FUSIONNES le 2026-08-21 (demande du dev) : les deux
   blocs disaient la meme chose l'un sous l'autre. Ordre impose : le nombre
   d'heures d'abord, la lecture en passages ensuite, la consequence pratique
   en dernier. Le verdict d'impossibilite reste un bloc a part, il ne porte
   aucune duree. */
function rendBig(r){
  const p = r.passage;
  if (p.type === "impossible") return "";
  const tone = (p.total <= 32) ? "ok" : "warn";
  const detail = (r.nLots === 1)
    ? "un seul passage de " + fmtJours(p.total)
    : r.nLots + " passages, soit " + fmtJours(r.total) + " en tout";
  const conseq = (r.nLots === 1)
    ? "Tout tient en une fois, dans un appareil rempli aux deux tiers."
    : "Vos affaires ne tiennent pas en une fois. Il faut <b>"+r.nLots+" passages</b> de <b>"+fmtJours(p.total)+"</b> chacun, "+
      "l'un après l'autre.";
  return '<div class="apc-big apc-big--'+tone+'">'+
    '<p class="apc-big__l">Durée totale du traitement</p>'+
    '<p class="apc-big__v">'+Math.round(r.total)+' h</p>'+
    '<p class="apc-big__u">'+detail+'</p>'+
    '<p class="apc-big__c">'+conseq+'</p></div>';
}

function rendPassage(r){
  const p = r.passage;
  if (p.type === "impossible") return "";
  let h = '<div class="apc-card"><p class="apc-card__t">Ce que dure un passage, et pourquoi</p>';

  if (p.type === "descente+expo"){
    const wt1 = Math.max(18, Math.round(p.miseACoeur / p.total * 100));
    h += '<div class="apc-decomp">'+
      '<div class="apc-decomp__s apc-decomp__s--t1" style="width:'+wt1+'%"><p class="apc-decomp__v">'+p.miseACoeur+' h</p>'+
      '<p class="apc-decomp__n">pour que le froid atteigne le cœur de vos affaires</p></div>'+
      '<div class="apc-decomp__s apc-decomp__s--t2" style="flex:1"><p class="apc-decomp__v">'+p.expo+' h</p>'+
      '<p class="apc-decomp__n">de froid réel, une fois qu\'elles y sont</p></div></div>';
    h += '<p style="font-size:12.5px;line-height:1.6;margin:0 0 4px;color:var(--color-gray-medium)">'+
      '<b>Le compteur ne démarre pas à la fermeture de la porte.</b> Vos affaires entrent tièdes : il leur faut '+
      'environ '+p.miseACoeur+' h pour être froides jusqu\'au centre. Ce n\'est qu\'ensuite que les '+p.expo+' h '+
      'qui tuent commencent. C\'est l\'erreur la plus répandue sur le sujet, et elle fait rater des traitements.</p>';
    h += '<div class="apc-flag apc-flag--info">Ces '+p.miseACoeur+' h comptent <b>à chaque passage</b>, pas seulement au premier : '+
      'chaque nouveau chargement réintroduit des affaires à température ambiante.</div>';
  } else {
    h += '<div class="apc-decomp"><div class="apc-decomp__s apc-decomp__s--all">'+
      '<p class="apc-decomp__v">'+p.total+' h</p>'+
      '<p class="apc-decomp__n">de séjour complet, mise à cœur comprise</p></div></div>';
    h += '<p style="font-size:12.5px;line-height:1.6;margin:0 0 4px;color:var(--color-gray-medium)">'+
      'Ici la durée est donnée <b>tout compris</b> : elle part de la fermeture de la porte et inclut déjà le temps '+
      'que met le froid à atteindre le cœur de vos affaires. Sa marge absorbe aussi les cycles de dégivrage.</p>';
  }
  h += '<div class="apc-flag apc-flag--info"><b>Sur quoi repose ce chiffre.</b> '+esc(p.src)+'</div>';
  if (p.plancher) h += '<div class="apc-flag">⚠ À cette température, la durée affichée est un <b>plancher</b> : la seule étude qui mesure ne descend pas jusque-là.</div>';

  if (p.conditions.length){
    h += '<p class="apc-card__t" style="margin-top:14px">Cette durée courte ne vaut que si tout est réuni</p>'+
      '<ul class="apc-list apc-list--chk">'+p.conditions.map(c => '<li>'+esc(c)+'</li>').join('')+'</ul>'+
      '<div class="apc-flag">Si l\'une de ces conditions saute, revenez à <b>72 h</b>. C\'est ce que recommandent le CNEV, '+
      'l\'Assurance Maladie et la Ville de Paris pour un congélateur ménager ordinaire.</div>';
  } else if (p.manque.length && p.total > 32){
    h += '<div class="apc-flag">Il existe une durée bien plus courte, <b>32 h</b>, mais elle suppose des conditions que vous ne remplissez pas : '+
      esc(p.manque.join(" ; "))+'.</div>';
  }
  return h + '</div>';
}

function rendCampagne(r){
  if (r.passage.type === "impossible") return "";
  let h = '<div class="apc-card"><p class="apc-card__t">D\'où sort le nombre de passages</p><ul class="apc-list">';
  h += '<li>Vos affaires : <b>'+r.cfg.vObjets+' L</b></li>';
  h += '<li>Votre congélateur : '+r.cfg.vUtile+' L, dont <b>'+r.vLot+' L</b> réellement utilisables (les deux tiers, pour que l\'air circule)</li>';
  h += '<li>Donc <b>'+r.nLots+' passage'+(r.nLots>1?'s':'')+'</b> de '+Math.round(r.passage.total)+' h</li>';
  h += '<li><b>Total : '+Math.round(r.total)+' h</b>, soit '+fmtJours(r.total)+'</li>';
  h += '</ul><div class="apc-flag">⚠ La limite des deux tiers est une <b>prudence de notre part</b>, elle n\'est mesurée nulle part. '+
       'Elle traduit une consigne qualitative : ne tassez pas, l\'air froid doit passer entre les objets.</div>';
  if (r.total/24 >= 14){
    h += '<div class="apc-flag">Une campagne de '+Math.round(r.total/24)+' jours n\'est pas un échec de l\'outil, c\'est un résultat : '+
      'à ce volume, le congélateur n\'est plus la bonne méthode. Voir les autres voies plus bas.</div>';
  }
  return h + '</div>';
}

function rendCorpus(r){
  if (r.passage.type === "impossible") return "";
  let h = '<div class="apc-card"><p class="apc-card__t">Pourquoi les chiffres trouvés ailleurs se contredisent</p>'+
    '<p style="font-size:12.5px;line-height:1.6;margin:0 0 8px;color:var(--color-gray-medium)">'+
    'À -20 °C, six sources publiques françaises donnent six durées différentes, de 2 h à 72 h. Elles ne se contredisent '+
    'qu\'en apparence : <b>les unes comptent à partir du moment où l\'objet est froid à cœur, les autres comptent le séjour complet</b>. '+
    'Nous retenons l\'Anses, la seule qui pose explicitement la condition.</p><table class="apc-tbl">'+
    '<tr><th>Source</th><th>À -20 °C</th><th>Ce que ce chiffre compte</th></tr>';
  CORPUS_20.forEach(c => {
    h += '<tr'+(c.ref === "FRO26" ? ' class="is-mine"' : '')+'><td>'+esc(c.qui)+'</td><td>'+esc(c.val)+'</td><td>'+esc(c.note)+'</td></tr>';
  });
  return h + '</table></div>';
}

function rendAvertissements(r){
  let h = "";
  const avs = r.choisis.filter(c => c.avert);
  if (r.refuses.length){
    h += '<div class="apc-card"><p class="apc-card__t">À sortir du sac</p>';
    r.refuses.forEach(c => { h += '<div class="apc-flag apc-flag--ko">⛔ <b>'+esc(c.nom)+'</b> - '+esc(c.avert)+'</div>'; });
    h += '</div>';
  }
  const denses = avs.filter(c => c.dense);
  if (denses.length){
    h += '<div class="apc-card"><p class="apc-card__t">Ce qui peut faire échouer le traitement</p>'+
      '<p style="font-size:12.5px;line-height:1.6;margin:0 0 8px;color:var(--color-gray-medium)">'+
      'Nous ne savons pas de combien allonger la durée pour ces objets : <b>personne ne l\'a mesuré</b>. Nous préférons '+
      'vous le dire plutôt que d\'inventer un chiffre.</p>';
    denses.forEach(c => { h += '<div class="apc-flag">⚠ <b>'+esc(c.nom)+'</b> - '+esc(c.avert)+'</div>'; });
    h += '<div class="apc-flag apc-flag--info"><b>Le geste qui règle le problème : une sonde de température</b>, posée '+
      'au contact des objets et pas dans l\'air. C\'est le seul moyen de savoir si le froid est vraiment arrivé où il doit. '+
      'Sans elle, ces durées restent des estimations prudentes.</div></div>';
  }
  return h;
}

function rendProtocole(r){
  if (r.passage.type === "impossible") return "";
  return '<div class="apc-card"><p class="apc-card__t">Le protocole, dans l\'ordre</p>'+
    '<ol class="apc-list apc-list--num">'+
    '<li>Remplissez le sac <b>dans la pièce infestée</b>, jamais ailleurs.</li>'+
    '<li>Déposez sans tasser, en laissant un bon tiers de vide.</li>'+
    '<li>Fermez le sac hermétiquement, puis sortez-le de la pièce.</li>'+
    '<li>Congelez la durée entière <b>sans ouvrir la porte</b> : chaque ouverture relance le compteur de mise à cœur.</li>'+
    '<li>Ne rouvrez le sac qu\'<b>après retour à température ambiante</b>.</li></ol>'+
    '<div class="apc-flag apc-flag--ko">⛔ <b>Le balcon, la cave, le garage et le coffre de la voiture ne traitent rien</b>, '+
    'même en plein hiver. La punaise survit au-dessus de -12 °C, et ces endroits n\'y descendent jamais de façon continue.</div></div>';
}

/* ==========================================================================
   Marquage UTM des liens sortants du composant
   --------------------------------------------------------------------------
   Demande du dev du 2026-08-21 : savoir ce que le simulateur genere comme
   achats. Parametres imposes : website / simulator / freeze-simulator.
   (Le simulateur de cout utilisera treatment-simulator sur les deux memes
   premiers champs.)

   ⚠ Ce sont des liens INTERNES. Un utm_source sur un lien interne REECRIT la
   source de la session : un visiteur venu de Google organique qui clique une
   carte devient « website / simulator » dans GA4 et dans l'attribution
   Shopify. On gagne l'attribution des achats au simulateur, on perd le canal
   d'acquisition reel de ces sessions. Arbitrage assume par le dev.

   Les liens vers un ARTICLE ne sont pas marques : aucun achat n'y est
   possible, et chaque marquage coute une source d'acquisition. Pour eux, la
   mesure du clic passerait plutot par la collecte du composant.
   ========================================================================== */
const UTM = "utm_source=website&utm_medium=simulator&utm_campaign=freeze-simulator";
function marque(u){
  if (u.indexOf('/blogs/') !== -1) return u;          /* article : pas de marquage */
  return u + (u.indexOf('?') === -1 ? '?' : '&') + UTM;
}

/* --- Dernière section : toujours affichée -------------------------------- */
function rendAlternatives(r){
  const p = r.passage;
  let intro;
  if (p.type === "impossible"){
    intro = "Votre congélateur ne peut rien pour vous, mais vous n'êtes pas sans solution. Voici ce qui traite vraiment.";
  } else if (r.total && r.total/24 >= 7){
    intro = "Votre traitement au froid demande "+Math.round(r.total/24)+" jours. C'est long, et ce n'est pas toujours la meilleure route. Voici les autres.";
  } else {
    intro = "Le congélateur ne traite que ce qui rentre dedans. Pour le matelas, le sommier, les meubles et tout ce qui reste dans la pièce, il faut autre chose.";
  }

  let h = '<div class="apc-card"><p class="apc-card__t">Quelles sont les alternatives pour traiter</p>'+
    '<p style="font-size:12.5px;line-height:1.6;margin:0 0 12px;color:var(--color-gray-medium)">'+esc(intro)+'</p>'+
    '<div class="apc-alt">';

  ALTERNATIVES.forEach(a => {
    h += '<div class="apc-alt__c">'+
      '<img class="apc-alt__img" src="'+a.img+'" alt="'+esc(a.alt)+'" loading="lazy">'+
      '<div class="apc-alt__b">'+
      '<p class="apc-alt__t">'+esc(a.titre)+'</p>'+
      '<p class="apc-alt__d">'+esc(a.desc)+'</p>'+
      '<p class="apc-alt__n">'+esc(a.note)+'</p>'+
      a.liens.map(l => '<a class="apc-alt__l" href="'+marque(l.u)+'">'+esc(l.t)+'</a>').join('')+
      '</div></div>';
  });

  h += '</div>';
  return h + '</div>';
}

/* DEBUG UNIQUEMENT - plus appelee depuis le 2026-08-21 (demande du dev).
   Conservee pour verifier un calcul a la console : rendTrace(simule(lisConfig())). */
function rendTrace(r){
  const p = r.passage; let rows = "";
  const row = (k,v) => { rows += '<tr><td>'+k+'</td><td>'+v+'</td></tr>'; };
  row("V_objets", r.cfg.vObjets+' L  ('+r.cfg.detailGab+')');
  row("V_utile", r.cfg.vUtile+' L');
  row("V_lot", r.vLot+' L  = '+r.cfg.vUtile+' x '+TAUX_REMPLISSAGE);
  row("N_lots", 'ceil('+r.cfg.vObjets+'/'+r.vLot+') = <span class="hi">'+r.nLots+'</span>');
  row("T_appareil", '<span class="hi">'+r.tApp+' °C</span> ('+r.cfg.origineT+')');
  row("4 étoiles", r.cfg.quatreEtoiles ? '<span class="ok">oui</span>' : '<span class="ko">non</span>');
  row("frost-free", r.cfg.frostFree ? '<span class="ko">oui</span>' : '<span class="ok">non</span>');
  row("objets denses", r.denses.length ? '<span class="ko">oui ('+r.denses.map(c=>c.id).join(', ')+')</span>' : '<span class="ok">non</span>');
  if (r.refuses.length) row("écartés", '<span class="ko">'+r.refuses.map(c=>c.id).join(', ')+'</span>');
  row("barrière ≥ "+T_SURVIE+" °C", p.type === "impossible" ? '<span class="ko">FRANCHIE</span>' : '<span class="ok">passée</span>');
  if (p.type === "impossible") return traceBox(rows);
  row("branche", '<span class="ok">'+p.type+'</span>');
  if (p.type === "descente+expo"){
    row("mise à cœur", p.miseACoeur+' h  (FRO10, chaque passage)');
    row("exposition", p.expo+' h'+(p.plancher ? '  <span class="ko">[plancher extrapolé]</span>' : ''));
  }
  row("durée passage", '<span class="hi">'+p.total+' h</span>');
  if (p.manque.length && p.total > 32) row("branche courte", '<span class="ko">écartée : '+esc(p.manque.join(' ; '))+'</span>');
  row("TOTAL", '<span class="hi">'+r.total+' h</span> = '+r.nLots+' x '+p.total+'  ('+jours(r.total)+' j)');
  return traceBox(rows);
}
function traceBox(rows){
  return '<div class="apc-trace"><p class="apc-trace__t">Comment cette duree est calculee</p><table>'+rows+'</table></div>';
}

/* ==========================================================================
   Lecture du formulaire
   ========================================================================== */
function lisConfig(){
  const dedie = $('input[name=appareil]:checked').value === "dedie";
  const tmode = $('input[name=tmode]:checked').value;
  let temps = [], origineT = "", quatreEtoiles = dedie;

  if (tmode === "mesure"){
    temps = [parseInt($('#t-mesuree').value,10)];
    origineT = "mesurée";
    if (!dedie) quatreEtoiles = (parseInt($('#etoiles').value,10) === 4);
  } else if (tmode === "etoiles" && !dedie){
    const e = parseInt($('#etoiles').value,10);
    temps = [T_ETOILES[e]]; origineT = e+" étoile(s), règlement UE 2019/2016";
    quatreEtoiles = (e === 4);
  } else if (tmode === "classe"){
    temps = [-18];
    origineT = dedie ? "congélateur dédié = 4 étoiles par définition (règlement UE, art. 2 pt 11)" : "classe déclarée";
  } else {
    temps = [-18,-12]; origineT = "inconnue, deux scénarios";
  }

  let vObjets = 0, detail = [], compte = {};
  GABARITS.forEach(g => {
    const n = parseInt($('#nb-'+g.id).value,10) || 0;
    compte[g.id] = n;
    if (n){ vObjets += n*g.litres; detail.push(n+' x '+g.litres+'L'); }
  });
  const autreLibelle = ($('#autre-libelle').value || '').trim().slice(0,60);
  const autreLitres  = parseInt($('#autre-litres').value,10) || 0;
  if (autreLitres){ vObjets += autreLitres; detail.push('autre ' + autreLitres + 'L'); }

  const gab = $('#gabarit-appareil').value;
  return {
    temps, origineT, quatreEtoiles,
    frostFree: $('input[name=frost]:checked').value !== "statique",
    vUtile: gab === "custom" ? (parseInt($('#vol-utile').value,10)||114) : parseInt(gab,10),
    vObjets: Math.max(vObjets,1),
    detailGab: detail.join(' + ') || 'aucun',
    compte, autreLibelle, autreLitres,
    tMode: tmode,
    appareil: dedie ? 'dedie' : 'compartiment',
    etoiles: dedie ? '' : parseInt($('#etoiles').value,10),
    frost: $('input[name=frost]:checked').value,
    piege: ($('#site').value || ''),
    contenu: $$('#q-contenu input:checked').map(i => i.value)
  };
}

/* ==========================================================================
   COLLECTE - un envoi par visite, au moment où le résultat se stabilise
   --------------------------------------------------------------------------
   Contrat repris de simulateur-cout/DEPLOIEMENT.md § 5, sans y déroger :
     - PAS d'en-tête Content-Type : le navigateur envoie text/plain, ce qui
       évite le preflight OPTIONS qu'Apps Script ne sait pas traiter.
     - keepalive : l'envoi survit à la navigation sortante (clic sur une carte).
     - Aucune donnée personnelle. Pas d'email, pas de nom, pas d'IP conservée.
     - Un seul envoi par visite : le recalcul met à jour la charge en attente,
       elle ne part qu'au départ de la page. Sinon une visite pèse dix lignes.
   ⚠ URL_COLLECTE reste vide tant que le déploiement n'est pas fait. Vide,
   rien n'est envoyé et l'outil fonctionne normalement. Au portage dans Focal,
   cette valeur devient un réglage de section, pas une constante de code.
   Voir DEPLOIEMENT.md dans OUTILS/simulateur-congelation/.
   ========================================================================== */
/* Les reglages viennent du metaobjet `simulateur` (handle porte par le marqueur),
   serialises par le snippet dans un <script type="application/json">. Jamais de
   constante en dur : changer d'URL est la seule parade en cas d'arrosage, et il
   n'existe aucun blocage d'IP sur Apps Script. Voir la note simulateurs.md. */
const CFG = (() => {
  try { return JSON.parse(document.getElementById('apc-config').textContent) || {}; }
  catch (e) { return {}; }
})();
const URL_COLLECTE   = CFG.url_collecte || "";
const MODELE_VERSION = CFG.version || "";
let chargeEnAttente = null, dejaEnvoye = false;

function prepareEnvoi(cfg, r){
  const p = r.passage;
  chargeEnAttente = {
    v_objets: cfg.vObjets,
    nb_sac: cfg.compte.sac, nb_valise: cfg.compte.valise, nb_carton: cfg.compte.carton,
    autre_libelle: cfg.autreLibelle, autre_litres: cfg.autreLitres,
    v_utile: cfg.vUtile,
    appareil: cfg.appareil,
    t_mode: cfg.tMode,
    t_appareil: r.tApp,
    etoiles: cfg.etoiles,
    frost: cfg.frost,
    contenu: cfg.contenu.join(','),
    branche: p.type === "descente+expo" ? "descente_expo" : p.type,
    duree_passage: p.total,
    nb_passages: r.nLots,
    duree_totale: r.total === null ? 0 : r.total,
    modele_version: MODELE_VERSION,
    article: location.pathname,
    site: cfg.piege                 // champ piège : vide chez un humain
  };
}

function envoie(){
  if (!URL_COLLECTE || !chargeEnAttente || dejaEnvoye) return;
  dejaEnvoye = true;
  try {
    fetch(URL_COLLECTE, { method:'POST', body: JSON.stringify(chargeEnAttente), keepalive: true });
  } catch (e) { /* la collecte ne doit jamais casser l'outil */ }
}

/* Au départ de la page, pas au recalcul. visibilitychange est le seul événement
   fiable sur mobile - pagehide et beforeunload y sont souvent ignorés. */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') envoie();
});
window.addEventListener('pagehide', envoie);

function calcule(){
  const cfg = lisConfig(), out = $('#out');
  if (!cfg.contenu.length){
    out.innerHTML = bloc("warn","Verdict","Dites-nous ce qu'il y a dans vos sacs",
      "Cela ne changera pas la durée calculée, mais certaines affaires ne doivent pas aller au congélateur, et d'autres demandent une vigilance particulière.");
    out.classList.add('is-on'); out.scrollIntoView({behavior:'smooth',block:'start'}); return;
  }
  let html = "";
  if (cfg.temps.length > 1){
    html += '<div class="apc-card"><p class="apc-card__t">Vous ne connaissez pas votre température</p>'+
      '<p style="font-size:12.5px;line-height:1.6;margin:0;color:var(--color-gray-medium)">Voici les deux extrêmes. '+
      'L\'écart entre eux est la meilleure raison d\'acheter un thermomètre de congélateur : c\'est la seule donnée qui '+
      'décide si votre traitement marche ou ne marche pas du tout.</p></div>';
    cfg.temps.forEach(t => {
      const r = simule(Object.assign({},cfg,{tApp:t}));
      html += '<div class="apc-scen"><p class="apc-scen__t">Si votre appareil est à '+t+' °C</p>'+
        rendVerdict(r)+rendBig(r)+'</div>';
    });
    const r0 = simule(Object.assign({},cfg,{tApp:cfg.temps[0]}));
    prepareEnvoi(cfg, r0);
    html += rendPassage(r0)+rendCampagne(r0)+rendCorpus(r0)+rendAvertissements(r0)+rendProtocole(r0)+rendAlternatives(r0);
  } else {
    const r = simule(Object.assign({},cfg,{tApp:cfg.temps[0]}));
    prepareEnvoi(cfg, r);
    html += rendVerdict(r)+rendBig(r)+rendPassage(r)+rendCampagne(r)+
            rendCorpus(r)+rendAvertissements(r)+rendProtocole(r)+rendAlternatives(r);
  }
  out.innerHTML = html; out.classList.add('is-on');
  out.scrollIntoView({behavior:'smooth',block:'start'});
}

/* ==========================================================================
   Construction du formulaire
   ========================================================================== */
(function init(){
  if (!ROOT) return;   /* marqueur sans entree metaobjet : le snippet n'a rien rendu */
  $('#q-gabarits').innerHTML = GABARITS.map(g =>
    '<div class="apc-lig"><span class="apc-lig__n">'+esc(g.nom)+'<small>'+esc(g.sous)+'</small></span>'+
    '<input class="apc-in" style="max-width:74px" type="number" id="nb-'+g.id+'" value="'+g.def+'" min="0" max="60" step="1"></div>').join('');

  $('#q-contenu').innerHTML = CONTENU.map(c =>
    '<label class="apc-opt"><input type="checkbox" value="'+c.id+'"'+(c.id==='linge'?' checked':'')+'>'+
    '<span>'+esc(c.nom)+(c.refuse?' <b style="color:#9B2A22">- à ne pas congeler</b>':'')+'</span></label>').join('');

  function totalObjets(){
    let v = 0; GABARITS.forEach(g => { v += (parseInt($('#nb-'+g.id).value,10)||0) * g.litres; });
    v += parseInt($('#autre-litres').value,10) || 0;
    $('#tot-objets').textContent = 'Volume total : ' + v + ' L';
  }

  function sync(){
    const dedie = $('input[name=appareil]:checked').value === "dedie";
    const tmode = $('input[name=tmode]:checked').value;
    $('#row-mesure').style.display  = (tmode === "mesure") ? "flex" : "none";
    /* Un congélateur dédié est 4 étoiles par définition : la classe ne se demande pas */
    $('#row-etoiles').style.display = dedie ? "none" : "block";
    $('#opt-dedie18').querySelector('small').textContent = dedie
      ? "Nous retiendrons -18 °C, la valeur garantie par sa classe 4 étoiles"
      : "Nous retiendrons la température de la classe indiquée ci-dessus";
    if (dedie && tmode === "etoiles") $('input[name=tmode][value=classe]').checked = true;
    $('#flag-ff').style.display = ($('input[name=frost]:checked').value === "statique") ? "none" : "block";
    $('#row-vol').style.display = ($('#gabarit-appareil').value === "custom") ? "flex" : "none";
    $$('label.apc-opt').forEach(l => l.classList.toggle('is-on', !!l.querySelector('input').checked));
    totalObjets();
  }

  document.addEventListener('change', e => {
    if (e.target.id === 'gabarit-appareil' && e.target.value !== 'custom') $('#vol-utile').value = e.target.value;
    sync();
  });
  document.addEventListener('input', totalObjets);
  sync();
  $('#go').addEventListener('click', calcule);
})();
